/**
 * Godot's CanvasItem tint. `modulate` multiplies onto the node and every
 * descendant through context, and `self_modulate` onto the node's own pixels.
 * `useCanvasItemTint` resolves both, with the material colour converted from the
 * authored sRGB to linear, as Godot's canvas does.
 */
import { createContext, useContext, useMemo } from 'react';
import { useGodotLinearColor } from './godotColor';
export const WHITE_MODULATE = { r: 1, g: 1, b: 1, a: 1 };
export const Modulate2DContext = createContext(WHITE_MODULATE);
export function useParentModulate() {
    return useContext(Modulate2DContext);
}
/**
 * Component-wise RGBA multiply (Godot composes modulate by multiplication), in double, not
 * `multiplyColor`: the double keeps our sRGB round trip on Godot's output at a half step, so
 * `font_color` 0.9 writes 230 as Godot does. The test "keeps a 0.9 channel" pins it.
 */
export function multiplyModulate(a, b) {
    return { r: a.r * b.r, g: a.g * b.g, b: a.b * b.b, a: a.a * b.a };
}
/** Resolve a CanvasItem's modulate/self_modulate into a material tint + child-context value. */
export function useCanvasItemTint(props, 
/**
 * A tint on this item's own pixels that is not passed on: the canvas modulate,
 * which Godot multiplies in per item in the base pass.
 */
ownMultiplier = WHITE_MODULATE) {
    const parent = useParentModulate();
    const inherited = useMemo(() => multiplyModulate(parent, props.modulate), [parent, props.modulate]);
    const self = useMemo(() => multiplyModulate(inherited, props.self_modulate), [inherited, props.self_modulate]);
    const own = useMemo(() => multiplyModulate(self, ownMultiplier), [self, ownMultiplier]);
    const color = useGodotLinearColor(own);
    return { inherited, self, own, color, opacity: own.a };
}
//# sourceMappingURL=canvasItemModulate.js.map